import ReactFlow, { Background, Controls, MiniMap } from "reactflow";
import type { Node, Edge } from "reactflow";
import type { LineCanvas } from "@/types/canvas.types";
import type { CanvasNodeData } from "@/types/canvas.types";
import { nodeTypes } from "../nodes";
import { edgeTypes } from "../edges";
import { EditableEdgeProvider } from "../edges/EditableEdgeContext";
import type { CanvasFlowEvents } from "./hooks/useCanvasFlowEvents";

interface Props {
  canvas: LineCanvas;
  rfNodes: Node<CanvasNodeData>[];
  rfEdges: Edge[];
  draftNodes: Node<CanvasNodeData>[];
  isEdit: boolean;
  compact: boolean;
  hideMiniMap: boolean;
  flowEvents: CanvasFlowEvents;
  handleNodeClick: (e: unknown, node: Node<CanvasNodeData>) => void;
  scheduleViewportSave: (vp: { x: number; y: number; zoom: number }) => void;
}

export function CanvasReactFlowSurface({
  canvas,
  rfNodes,
  rfEdges,
  draftNodes,
  isEdit,
  compact,
  hideMiniMap,
  flowEvents,
  handleNodeClick,
  scheduleViewportSave,
}: Props) {
  return (
    <>
      {canvas.isFallback && !isEdit ? (
        <div className="text-rd-info-700 border-rd-info-500/30 absolute left-2 top-2 z-10 rounded-md border bg-rd-info-50 px-2 py-0.5 text-[10px] font-medium">
          Layout automático — clique em &quot;Editar&quot; para ajustar
        </div>
      ) : null}
      <EditableEdgeProvider value={{ isEdit, onDeleteEdge: flowEvents.onDeleteEdgeById }}>
        <ReactFlow
          nodes={rfNodes}
          edges={rfEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodesDraggable={isEdit}
          nodesConnectable={isEdit}
          elementsSelectable
          panOnDrag
          zoomOnScroll
          fitView={!isEdit}
          fitViewOptions={{ padding: 0.2, maxZoom: 1.5 }}
          defaultViewport={canvas.viewport ?? undefined}
          deleteKeyCode={isEdit ? ["Delete", "Backspace"] : null}
          isValidConnection={(c) => {
            const src = draftNodes.find((n) => n.id === c.source);
            const tgt = draftNodes.find((n) => n.id === c.target);
            return src?.type !== "GROUP" && tgt?.type !== "GROUP";
          }}
          onNodesChange={flowEvents.onNodesChange}
          onEdgesChange={flowEvents.onEdgesChange}
          onEdgesDelete={flowEvents.onEdgesDelete}
          onConnect={flowEvents.onConnect}
          onNodeClick={handleNodeClick}
          onMoveEnd={(_, viewport) => {
            if (!isEdit) scheduleViewportSave(viewport);
          }}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={16} size={1} />
          {!compact ? <Controls showInteractive={false} /> : null}
          {!compact && !hideMiniMap && rfNodes.length >= 6 ? (
            <MiniMap pannable zoomable position="bottom-right" />
          ) : null}
        </ReactFlow>
      </EditableEdgeProvider>
    </>
  );
}
